import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Pencil,
  Plus,
  Trash2,
  Upload,
  X,
} from "lucide-react";
import { ApiError, apiFetch, getAdminTeams } from "@/lib/api";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import {
  DataTable,
  DataTableBody,
  DataTableCell,
  DataTableEmptyRow,
  DataTableHead,
  DataTableHeadCell,
  DataTablePagination,
  DataTableRow,
  DataTableScroll,
  DataTableShell,
} from "@/components/ui/DataTable";
import { unscopedKey } from "@/lib/queryClient";
import type { Match, AdminStreamSession, Season, AdminTeamRow, MeTeam } from "@/lib/types";
import { useMe } from "@/features/auth/useMe";
import { useAuthStore } from "@/stores/auth";
import { MatchEditModal } from "@/features/matches/components/MatchEditModal";
import { TkticSelect } from "@/components/ui/TkticSelect";
import { useUploadQueueContext } from "@/features/coach/components/UploadQueueContext";
import type { UploadSlot } from "@/features/coach/components/useUploadQueue";
import {
  defaultSlotForMatch,
  SLOT_LABELS,
  slotsForMatch,
} from "@/features/matches/matchSlots";
import {
  useAdminStreams,
  useTranscodeProgress,
} from "../hooks/useAdminData";
import { AdminIconButton, AdminIconLink } from "../components/AdminIconButton";
import { AdminPageHeader } from "../components/AdminPageHeader";
import { MatchSlotPanel } from "./MatchSlotPanel";

/**
 * M8a B1–B9 — `/admin/matches`.
 *
 * Library table (8 columns) + filters + per-row expand to a slot
 * diagnostics panel with the operator recovery action set:
 *   - Verify / Regen HLS / Re-transcode / Retry / Force Re-transcode
 *   - Logs / Regenerate Thumbnail
 *   - Delete match (B7)
 *
 * VOD viewer counts read from the shell's already-running
 * `useAdminStreams()` cache, filtered to `kind='vod'` per match.
 * Live transcode progress is polled ONLY while at least one slot
 * is `transcoding` (the route gate avoids polling when idle).
 *
 * SCOPE — `/admin/matches` is visible to **global admins, team
 * admins, and uploaders**. It behaves like `/admin/people`: the
 * match row owns its team assignment, global admins can see/filter
 * all teams, and team-scoped operators only see teams they can
 * manage. Add/edit receives the same allowed-team list, so the page
 * does not depend on the admin shell's global team scope switcher.
 *
 * Calling `/api/matches` without `team_id` for a non-global user
 * would return the global recent-matches list and leak cross-tenant
 * match metadata, so non-global queries always pass scope.
 *
 * The Add/Edit modal (B6) includes a team selector for create;
 * existing matches keep their current team fixed.
 */
/** Stable empty-array identity so the `me.seasons` selector doesn't
 * return a fresh `[]` each render (which would re-fire `useMemo`). */
const EMPTY_SEASONS: Season[] = [];

type MatchTeamOption = Pick<AdminTeamRow | MeTeam, "id" | "name">;

const PAGE_SIZE_OPTIONS = [
  { value: "10", label: "10 / page" },
  { value: "25", label: "25 / page" },
  { value: "50", label: "50 / page" },
];

function normalizeTeamOptions<T extends MatchTeamOption>(
  teams: T[],
): Array<{ id: string; name: string }> {
  const byId = new Map<string, { id: string; name: string }>();
  for (const team of teams) {
    byId.set(team.id, { id: team.id, name: team.name });
  }
  return Array.from(byId.values()).sort((a, b) => a.name.localeCompare(b.name));
}

export function AdminMatchesPage() {
  const me = useMe().data;
  const isGlobalAdmin = !!me?.user?.is_global_admin;
  const meTeams = me?.teams ?? [];

  const unscopedMatchesQuery = useQuery({
    queryKey: unscopedKey("admin-matches"),
    queryFn: () => apiFetch<Match[]>("/api/matches", { scope: false }),
    enabled: isGlobalAdmin,
  });
  const adminTeamsQuery = useQuery({
    queryKey: unscopedKey("admin-teams"),
    queryFn: getAdminTeams,
    enabled: isGlobalAdmin,
    staleTime: 60_000,
  });
  const matchTeamOptions = useMemo(
    () =>
      isGlobalAdmin
        ? normalizeTeamOptions(adminTeamsQuery.data ?? [])
        : normalizeTeamOptions(
            meTeams.filter((team) => team.capabilities.includes("match:write")),
          ),
    [adminTeamsQuery.data, isGlobalAdmin, meTeams],
  );
  const matchTeamIds = useMemo(
    () => matchTeamOptions.map((team) => team.id).sort().join("|"),
    [matchTeamOptions],
  );
  const allowedMatchesQuery = useQuery({
    queryKey: unscopedKey("admin-matches-allowed", { teamIds: matchTeamIds }),
    queryFn: async () => {
      const perTeam = await Promise.all(
        matchTeamOptions.map((team) =>
          apiFetch<Match[]>("/api/matches", {
            scope: { teamId: team.id, seasonId: null },
          }),
        ),
      );
      const byId = new Map<string, Match>();
      for (const rows of perTeam) {
        for (const match of rows) byId.set(match.id, match);
      }
      return Array.from(byId.values());
    },
    enabled: !isGlobalAdmin && matchTeamOptions.length > 0,
  });
  const matchesQuery = isGlobalAdmin ? unscopedMatchesQuery : allowedMatchesQuery;
  const streamsQuery = useAdminStreams();
  const matches = matchesQuery.data ?? [];
  const teamNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const t of matchTeamOptions) map.set(t.id, t.name);
    return map;
  }, [matchTeamOptions]);

  // Gate transcode-progress polling on "any visible slot transcoding".
  const anyTranscoding = useMemo(() => {
    for (const m of matches) {
      for (const status of Object.values(m.video_status ?? {})) {
        if (status === "transcoding") return true;
      }
    }
    return false;
  }, [matches]);
  useTranscodeProgress({ enabled: anyTranscoding });

  // B2 — filters.
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<
    "all" | "none" | "ready" | "transcoding" | "error" | "partial"
  >("all");
  const [formatFilter, setFormatFilter] = useState<
    "all" | "full" | "two_halves"
  >("all");
  const [teamFilter, setTeamFilter] = useState<string>("all");
  const [seasonFilter, setSeasonFilter] = useState<string>("all");
  const [sort, setSort] = useState<"date_asc" | "date_desc" | "updated_desc">(
    "date_desc",
  );
  const [pageSize, setPageSize] = useState("10");
  const [page, setPage] = useState(1);

  // Season options come from the globally-shared `me.seasons` list —
  // the same source `SeasonLabel` resolves names from, so no extra
  // fetch. The default season is marked so the operator can tell which
  // one new matches inherit.
  const seasons = useAuthStore((s) => s.me?.seasons ?? EMPTY_SEASONS);
  const seasonOptions = useMemo(
    () => [
      { value: "all", label: "All seasons" },
      ...seasons.map((s) => ({
        value: s.id,
        label: s.is_default ? `${s.name} (default)` : s.name,
      })),
    ],
    [seasons],
  );

  const [expanded, setExpanded] = useState<string | null>(null);
  const [editTarget, setEditTarget] = useState<Match | "new" | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Match | null>(null);
  // Sequential multi-upload queue: per-row "Upload video" adds an item.
  // The queue lives in `UploadQueueProvider` at AdminShell level so it
  // (and the docked panel) survives navigation between admin sections.
  const uploadQueue = useUploadQueueContext();
  const [queueTarget, setQueueTarget] = useState<Match | null>(null);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    const filtered = matches.filter((m) => {
      if (term) {
        const haystack = [
          m.home_team,
          m.away_team,
          m.location,
          m.id,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!haystack.includes(term)) return false;
      }
      if (formatFilter !== "all" && deriveFormatGroup(m) !== formatFilter) {
        return false;
      }
      if (statusFilter !== "all") {
        const agg = deriveAggregateStatus(m);
        if (agg !== statusFilter) return false;
      }
      if (teamFilter !== "all") {
        const tid = m.team_id ?? "__unassigned__";
        if (tid !== teamFilter) return false;
      }
      if (seasonFilter !== "all" && m.season_id !== seasonFilter) {
        return false;
      }
      return true;
    });

    const sorted = [...filtered];
    sorted.sort((a, b) => {
      if (sort === "updated_desc") {
        return (b.updated_at ?? "").localeCompare(a.updated_at ?? "");
      }
      const da = a.date || "";
      const db = b.date || "";
      return sort === "date_asc" ? da.localeCompare(db) : db.localeCompare(da);
    });
    return sorted;
  }, [matches, search, statusFilter, formatFilter, teamFilter, seasonFilter, sort]);

  useEffect(() => {
    setPage(1);
  }, [
    search,
    statusFilter,
    formatFilter,
    teamFilter,
    seasonFilter,
    sort,
    pageSize,
  ]);

  const pageSizeNumber = Number(pageSize);
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSizeNumber));
  const safePage = Math.min(page, totalPages);
  const pageRows = useMemo(() => {
    const start = (safePage - 1) * pageSizeNumber;
    return filtered.slice(start, start + pageSizeNumber);
  }, [filtered, pageSizeNumber, safePage]);
  const firstVisible =
    filtered.length === 0 ? 0 : (safePage - 1) * pageSizeNumber + 1;
  const lastVisible = Math.min(filtered.length, safePage * pageSizeNumber);

  // Detect cross-tenant rows lacking a team_id so global admins can
  // filter for / spot orphaned matches that predate team scoping.
  // Team-scoped operators never see these because their reads are
  // fetched per allowed team.
  const hasUnassigned = useMemo(
    () => isGlobalAdmin && matches.some((m) => !m.team_id),
    [isGlobalAdmin, matches],
  );
  const teamFilterOptions = useMemo(
    () => [
      { value: "all", label: "All teams" },
      ...matchTeamOptions.map((t) => ({ value: t.id, label: t.name })),
      ...(hasUnassigned
        ? [{ value: "__unassigned__", label: "(no team)" }]
        : []),
    ],
    [hasUnassigned, matchTeamOptions],
  );
  useEffect(() => {
    if (teamFilter === "all") return;
    if (!teamFilterOptions.some((option) => option.value === teamFilter)) {
      setTeamFilter("all");
    }
  }, [teamFilter, teamFilterOptions]);

  return (
    <div data-testid="admin-matches" className="flex flex-col gap-3">
      <AdminPageHeader
        eyebrow="Tenants"
        title="Matches"
        description="Create team-owned match rows, edit metadata, upload video, and recover processing slots."
        meta={
          matchesQuery.isFetching
            ? "Refreshing…"
            : `${firstVisible}-${lastVisible} of ${filtered.length}`
        }
        actions={
          <button
            type="button"
            onClick={() => setEditTarget("new")}
            disabled={matchTeamOptions.length === 0}
            data-testid="admin-matches-add"
            aria-label="Add match"
            title={
              matchTeamOptions.length === 0
                ? "No manageable teams available"
                : "Add match"
            }
            className="inline-flex h-8 w-8 items-center justify-center rounded border border-accent bg-accent text-accent-ink transition-colors hover:bg-accent-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-canvas disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Plus size={16} strokeWidth={2} aria-hidden="true" />
          </button>
        }
      />

      {matchesQuery.error ? (
        <p
          data-testid="admin-matches-error"
          className="text-destructive font-mono text-xs"
        >
          Failed to load matches.
        </p>
      ) : null}

      {/* B2 — filters. One horizontal control row: search + team,
       * status, format, season, and sort filters. The search field
       * `grow`s to absorb all free width, so the fixed-width selects
       * sit flush against the right edge with no dead gap in the
       * middle. `flex-wrap` still lets it reflow on narrow widths.
       *
       * Each `TkticSelect` is wrapped in a fixed-width `div`. The
       * select's trigger is `w-full` by design (fills its parent);
       * dropping a `w-*` onto the trigger via `className` does NOT
       * reliably override `w-full` — both classes have equal
       * specificity and Tailwind's stylesheet order decides, so
       * `w-full` keeps winning and every select stretches to 100% of
       * the row and wraps. Sizing the WRAPPER instead makes `w-full`
       * resolve to the wrapper's width, which is what we want. */}
      <div className="flex flex-wrap items-center gap-2 rounded border border-rule bg-surface shadow-sm p-2">
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search teams / location / id"
          data-testid="admin-matches-search"
          className="bg-surface border border-rule rounded px-2 py-1.5 text-xs grow min-w-[12rem] focus:outline-none focus:ring-2 focus:ring-accent"
        />
        <div className="w-full sm:w-36">
          <TkticSelect
            value={statusFilter}
            onChange={(next) => setStatusFilter(next as typeof statusFilter)}
            data-testid="admin-matches-status-filter"
            size="sm"
            ariaLabel="Filter by status"
            options={[
              { value: "all", label: "All status" },
              { value: "ready", label: "Ready" },
              { value: "transcoding", label: "Transcoding" },
              { value: "error", label: "Error" },
              { value: "partial", label: "Partial" },
              { value: "none", label: "No video" },
            ]}
          />
        </div>
        <div className="w-full sm:w-32">
          <TkticSelect
            value={formatFilter}
            onChange={(next) => setFormatFilter(next as typeof formatFilter)}
            data-testid="admin-matches-format-filter"
            size="sm"
            ariaLabel="Filter by format"
            options={[
              { value: "all", label: "Any format" },
              { value: "full", label: "Full" },
              { value: "two_halves", label: "Halves" },
            ]}
          />
        </div>
        <div className="w-full sm:w-44">
          <TkticSelect
            value={seasonFilter}
            onChange={(next) => setSeasonFilter(next)}
            data-testid="admin-matches-season-filter"
            size="sm"
            ariaLabel="Filter by season"
            options={seasonOptions}
          />
        </div>
        {isGlobalAdmin || matchTeamOptions.length > 1 || hasUnassigned ? (
          <div className="w-full sm:w-44">
            <TkticSelect
              value={teamFilter}
              onChange={(next) => setTeamFilter(next)}
              data-testid="admin-matches-team-filter"
              size="sm"
              ariaLabel="Filter by team"
              options={teamFilterOptions}
            />
          </div>
        ) : null}
        <div className="w-full sm:w-44">
          <TkticSelect
            value={sort}
            onChange={(next) => setSort(next as typeof sort)}
            data-testid="admin-matches-sort"
            size="sm"
            ariaLabel="Sort matches"
            options={[
              { value: "date_desc", label: "Newest first" },
              { value: "date_asc", label: "Oldest first" },
              { value: "updated_desc", label: "Recently updated" },
            ]}
          />
        </div>
        <div className="w-full sm:w-32">
          <TkticSelect
            value={pageSize}
            onChange={setPageSize}
            data-testid="admin-matches-page-size"
            size="sm"
            ariaLabel="Rows per page"
            options={PAGE_SIZE_OPTIONS}
          />
        </div>
      </div>

      {/* B1 — library table */}
      <DataTableShell className="hidden md:block">
        <DataTableScroll>
          <DataTable data-testid="admin-matches-table">
          <DataTableHead>
            <DataTableRow interactive={false}>
              <DataTableHeadCell className="w-6"></DataTableHeadCell>
              <DataTableHeadCell>Date</DataTableHeadCell>
              <DataTableHeadCell>Team</DataTableHeadCell>
              <DataTableHeadCell>Matchup</DataTableHeadCell>
              <DataTableHeadCell>Format</DataTableHeadCell>
              <DataTableHeadCell>Slots</DataTableHeadCell>
              <DataTableHeadCell>Score</DataTableHeadCell>
              <DataTableHeadCell>Watching</DataTableHeadCell>
              <DataTableHeadCell align="right">Actions</DataTableHeadCell>
            </DataTableRow>
          </DataTableHead>
          <DataTableBody>
            {filtered.length === 0 ? (
              <DataTableEmptyRow colSpan={9} data-testid="admin-matches-empty">
                No matches.
              </DataTableEmptyRow>
            ) : null}
            {pageRows.map((m) => {
              const isOpen = expanded === m.id;
              const watchers = countVodViewers(streamsQuery.data?.active, m.id);
              const teamName = m.team_id
                ? teamNameById.get(m.team_id) ?? m.team_id
                : null;
              return (
                <FragmentRow
                  key={m.id}
                  match={m}
                  isOpen={isOpen}
                  onToggle={() => setExpanded(isOpen ? null : m.id)}
                  onEdit={() => setEditTarget(m)}
                  onDelete={() => setDeleteTarget(m)}
                  onUpload={() => setQueueTarget(m)}
                  watchers={watchers}
                  showTeamColumn
                  teamName={teamName}
                />
              );
            })}
          </DataTableBody>
        </DataTable>
        </DataTableScroll>
      </DataTableShell>
      <div className="flex flex-col gap-2 md:hidden" data-testid="admin-matches-cards">
        {filtered.length === 0 ? (
          <p
            data-testid="admin-matches-empty-mobile"
            className="rounded border border-rule bg-surface shadow-sm px-3 py-6 text-center text-ink-muted"
          >
            No matches.
          </p>
        ) : null}
        {pageRows.map((m) => {
          const isOpen = expanded === m.id;
          const watchers = countVodViewers(streamsQuery.data?.active, m.id);
          const teamName = m.team_id
            ? teamNameById.get(m.team_id) ?? m.team_id
            : null;
          return (
            <AdminMatchCard
              key={m.id}
              match={m}
              isOpen={isOpen}
              onToggle={() => setExpanded(isOpen ? null : m.id)}
              onEdit={() => setEditTarget(m)}
              onDelete={() => setDeleteTarget(m)}
              onUpload={() => setQueueTarget(m)}
              watchers={watchers}
              showTeamColumn
              teamName={teamName}
            />
          );
        })}
      </div>

      {filtered.length > 0 ? (
        <DataTablePagination
          first={firstVisible}
          last={lastVisible}
          total={filtered.length}
          page={safePage}
          totalPages={totalPages}
          onPrevious={() => setPage((p) => Math.max(1, p - 1))}
          onNext={() => setPage((p) => Math.min(totalPages, p + 1))}
          summaryTestId="admin-matches-pagination-summary"
          previousTestId="admin-matches-prev-page"
          indicatorTestId="admin-matches-page-indicator"
          nextTestId="admin-matches-next-page"
        />
      ) : null}

      {/* B6 — add/edit match form (M12b: extracted into shared
       * `MatchEditModal` so the coach Matches tab + admin Matches page
       * share the same modal). */}
      {editTarget ? (
        <MatchEditModal
          match={editTarget === "new" ? null : editTarget}
          teamOptions={matchTeamOptions}
          onClose={() => setEditTarget(null)}
        />
      ) : null}

      {/* B7 — delete confirm */}
      {deleteTarget ? (
        <DeleteMatchConfirm
          match={deleteTarget}
          onClose={() => setDeleteTarget(null)}
        />
      ) : null}

      {/* Per-row "Upload video" → slot + file picker → adds to the queue.
       * The docked `UploadQueuePanel` is rendered by `AdminShell`, not
       * here, so it persists across admin route changes. */}
      {queueTarget ? (
        <AddToQueueModal
          match={queueTarget}
          onAdd={(slot, file) => {
            uploadQueue.enqueue(queueTarget, slot, file);
            setQueueTarget(null);
          }}
          onClose={() => setQueueTarget(null)}
        />
      ) : null}
    </div>
  );
}

/** Slot + file picker for adding one video to the upload queue. Kept
 * deliberately small — the heavy chunked-upload UX lives in the docked
 * `UploadQueuePanel`; this modal just captures {slot, file}.
 *
 * Focus handling mirrors `CoachMatchUploadModal`: focus the first field
 * on open, restore focus to the trigger on close, window-level Escape.
 * Tab is contained within the dialog so a keyboard user can't drift
 * onto the (aria-hidden-equivalent) background behind an `aria-modal`. */
function AddToQueueModal({
  match,
  onAdd,
  onClose,
}: {
  match: Match;
  onAdd: (slot: UploadSlot, file: File) => void;
  onClose: () => void;
}) {
  // Slot options follow the match's video layout — a `two_halves`
  // match has no `full` slot, so offering it would queue an upload
  // for a slot the match can't have. State seeds to the match's
  // first valid slot.
  const slotOptions = slotsForMatch(match);
  const [slot, setSlot] = useState<UploadSlot>(
    () => defaultSlotForMatch(match) as UploadSlot,
  );
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const dialogRef = useRef<HTMLFormElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  // Initial focus + restore-on-close. The trigger is the row's
  // "Upload video" button; returning focus there keeps keyboard nav
  // coherent after the modal unmounts.
  useEffect(() => {
    previousFocusRef.current =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    fileInputRef.current?.focus();
    const previousFocus = previousFocusRef.current;
    return () => {
      previousFocus?.focus();
    };
  }, []);

  // Window-level Escape so the modal closes even when focus has not
  // (yet) landed inside the overlay subtree.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Tab containment: wrap focus between the first and last focusable
  // element inside the dialog so an `aria-modal` user can't tab onto
  // the background.
  const onDialogKeyDown = (event: React.KeyboardEvent<HTMLFormElement>) => {
    if (event.key !== "Tab") return;
    const root = dialogRef.current;
    if (!root) return;
    const focusable = root.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
    );
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;
    if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const onSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!file) {
      setError("Pick a video file (.mp4 or .mkv) first.");
      return;
    }
    onAdd(slot, file);
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Add match video to upload queue"
      data-testid="admin-add-to-queue-modal"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      className="fixed inset-0 z-[140] flex items-end justify-center bg-black/80 p-3 sm:items-center sm:p-4"
    >
      <form
        ref={dialogRef}
        onSubmit={onSubmit}
        onKeyDown={onDialogKeyDown}
        className="flex max-h-[calc(100dvh-1.5rem)] w-full max-w-md flex-col gap-4 overflow-y-auto rounded border border-rule bg-surface p-4 pb-[calc(1rem+env(safe-area-inset-bottom))] shadow-pop sm:p-5"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="text-base font-display uppercase tracking-wider text-ink">
              Queue match video
            </h3>
            <p className="mt-1 truncate text-xs text-ink-muted">
              {match.home_team || "—"} vs {match.away_team || "—"}
              {match.date ? ` · ${match.date}` : ""}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="admin-add-to-queue-close"
            aria-label="Close"
            title="Close"
            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded border border-rule text-ink-muted transition-colors hover:bg-field hover:text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-canvas"
          >
            <X size={16} strokeWidth={2} aria-hidden="true" />
          </button>
        </div>

        <label className="flex flex-col gap-1 text-xs text-ink-muted">
          <span className="font-display uppercase tracking-wide">Slot</span>
          <TkticSelect
            value={slot}
            onChange={(next) => setSlot(next as UploadSlot)}
            data-testid="admin-add-to-queue-slot"
            ariaLabel="Slot"
            className="w-full"
            options={slotOptions.map((value) => ({
              value,
              label: SLOT_LABELS[value],
            }))}
          />
        </label>

        <label className="flex flex-col gap-1 text-xs text-ink-muted">
          <span className="font-display uppercase tracking-wide">
            Video file (.mp4 or .mkv)
          </span>
          {/* Native file pickers can't be replaced entirely (browser
            * security forbids triggering them from synthetic events),
            * but the `file:` Tailwind variants restyle the inner button
            * into a brand accent pill — same pattern as
            * `CoachMatchUploadModal` + `MatchEditModal`'s opponent-logo
            * picker. Mirrors the no-native-browser-chrome rule. */}
          <input
            ref={fileInputRef}
            type="file"
            accept=".mp4,.mkv,video/mp4,video/x-matroska"
            onChange={(e) => {
              setFile(e.target.files?.[0] ?? null);
              setError(null);
            }}
            data-testid="admin-add-to-queue-file"
            className="block w-full text-xs text-ink file:mr-2 file:rounded file:border-0 file:bg-accent/15 file:px-2 file:py-1 file:text-[0.65rem] file:font-display file:uppercase file:tracking-wider file:text-accent hover:file:bg-accent/25"
          />
          {file ? (
            <span className="mt-1 font-mono text-[0.6rem] text-ink-muted">
              {(file.size / 1024 / 1024).toFixed(1)} MB
            </span>
          ) : null}
        </label>

        {error ? (
          <p
            role="alert"
            data-testid="admin-add-to-queue-error"
            className="rounded border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive"
          >
            {error}
          </p>
        ) : null}

        <div className="flex flex-col-reverse gap-2 border-t border-rule pt-3 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-rule bg-canvas px-3 py-1.5 text-sm font-display uppercase tracking-wide text-ink hover:bg-field"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!file}
            data-testid="admin-add-to-queue-submit"
            className="rounded bg-accent px-3 py-1.5 text-sm font-display uppercase tracking-wide text-accent-ink hover:bg-accent-hover disabled:opacity-60"
          >
            Add to queue
          </button>
        </div>
      </form>
    </div>
  );
}

function FragmentRow({
  match,
  isOpen,
  onToggle,
  onEdit,
  onDelete,
  onUpload,
  watchers,
  showTeamColumn,
  teamName,
}: {
  match: Match;
  isOpen: boolean;
  onToggle: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onUpload: () => void;
  watchers: number;
  showTeamColumn: boolean;
  teamName: string | null;
}) {
  // Slots come from the match's video LAYOUT (`format`), not from
  // whatever keys `video_status` happens to carry — a pre-migration
  // `two_halves` match may still have a stale `full` key, and we
  // must not surface it.
  const slots = slotsForMatch(match);
  const aggStatus = deriveAggregateStatus(match);

  return (
    <>
      <DataTableRow
        data-testid="admin-matches-row"
        data-match-id={match.id}
        data-aggregate-status={aggStatus}
      >
        <DataTableCell className="w-6 align-top">
          <button
            type="button"
            onClick={onToggle}
            aria-expanded={isOpen}
            aria-label={isOpen ? "Collapse diagnostics" : "Expand diagnostics"}
            data-testid="admin-matches-row-expand"
            className="text-ink-muted hover:text-ink font-mono"
          >
            {isOpen ? "▼" : "▶"}
          </button>
        </DataTableCell>
        <DataTableCell className="align-top font-mono">
          {match.date || "—"}
          {match.time ? (
            <div className="text-[0.6rem] text-ink-muted">{match.time}</div>
          ) : null}
        </DataTableCell>
        {showTeamColumn ? (
          <DataTableCell
            data-testid="admin-matches-row-team"
            data-team-id={match.team_id ?? ""}
            className="align-top"
          >
            {teamName ? (
              <span className="text-ink">{teamName}</span>
            ) : (
              <span className="text-ink-muted italic">no team</span>
            )}
          </DataTableCell>
        ) : null}
        <DataTableCell className="align-top">
          <div className="text-ink">
            {match.home_team || "—"}{" "}
            <span className="text-ink-muted">vs</span>{" "}
            {match.away_team || "—"}
          </div>
          {match.location ? (
            <div className="text-[0.6rem] text-ink-muted">{match.location}</div>
          ) : null}
          <SeasonLabel match={match} />
        </DataTableCell>
        <DataTableCell className="align-top font-mono">
          <FormatPill format={match.format} />
        </DataTableCell>
        <DataTableCell className="align-top">
          <div className="flex flex-wrap gap-1">
            {slots.map((slot) => (
              <SlotPill
                key={slot}
                slot={slot}
                status={match.video_status?.[slot] ?? "none"}
              />
            ))}
          </div>
        </DataTableCell>
        <DataTableCell className="align-top font-mono">
          {match.score_home == null || match.score_away == null
            ? "—"
            : `${match.score_home}–${match.score_away}`}
        </DataTableCell>
        <DataTableCell className="align-top font-mono">
          <span
            data-testid="admin-matches-row-watchers"
            className={watchers > 0 ? "text-accent" : "text-ink-muted"}
          >
            {watchers}
          </span>
        </DataTableCell>
        <DataTableCell align="right" className="align-top">
          <div className="inline-flex gap-1">
            <AdminIconLink
              href={`/match/${match.slug}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="admin-matches-row-open"
              label="Open match"
              icon={ExternalLink}
            />
            <AdminIconButton
              onClick={onEdit}
              data-testid="admin-matches-row-edit"
              label="Edit match"
              icon={Pencil}
            />
            <AdminIconButton
              onClick={onUpload}
              data-testid="admin-matches-row-upload"
              label="Upload video"
              icon={Upload}
              tone="accent"
            />
            <AdminIconButton
              onClick={onDelete}
              data-testid="admin-matches-row-delete"
              label="Delete match"
              icon={Trash2}
              tone="destructive"
            />
          </div>
        </DataTableCell>
      </DataTableRow>
      {isOpen ? (
        <DataTableRow
          interactive={false}
          data-testid="admin-matches-row-diagnostics"
          data-match-id={match.id}
          className="bg-elevated"
        >
          <DataTableCell colSpan={showTeamColumn ? 9 : 8} className="p-3 align-top">
            <MatchSlotPanel match={match} />
          </DataTableCell>
        </DataTableRow>
      ) : null}
    </>
  );
}

function AdminMatchCard({
  match,
  isOpen,
  onToggle,
  onEdit,
  onDelete,
  onUpload,
  watchers,
  showTeamColumn,
  teamName,
}: {
  match: Match;
  isOpen: boolean;
  onToggle: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onUpload: () => void;
  watchers: number;
  showTeamColumn: boolean;
  teamName: string | null;
}) {
  // Slots come from the match's video LAYOUT (`format`), not from
  // whatever keys `video_status` happens to carry — a pre-migration
  // `two_halves` match may still have a stale `full` key, and we
  // must not surface it.
  const slots = slotsForMatch(match);
  const aggStatus = deriveAggregateStatus(match);

  return (
    <article
      data-testid="admin-matches-card"
      data-match-id={match.id}
      data-aggregate-status={aggStatus}
      className="rounded border border-rule bg-surface shadow-sm p-3"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-mono text-[0.65rem] uppercase tracking-wider text-ink-muted">
            {match.date || "No date"}
            {match.time ? <span> · {match.time}</span> : null}
          </p>
          <h3 className="mt-1 font-display text-base text-ink">
            {match.home_team || "—"}{" "}
            <span className="text-ink-muted">vs</span>{" "}
            {match.away_team || "—"}
          </h3>
          {match.location ? (
            <p className="mt-0.5 text-xs text-ink-muted">{match.location}</p>
          ) : null}
          {showTeamColumn ? (
            <p
              data-testid="admin-matches-card-team"
              data-team-id={match.team_id ?? ""}
              className="mt-1 text-xs text-ink-muted"
            >
              Team: <span className="text-ink">{teamName ?? "no team"}</span>
            </p>
          ) : null}
          <SeasonLabel match={match} />
        </div>
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={isOpen}
          aria-label={isOpen ? "Collapse diagnostics" : "Expand diagnostics"}
          title={isOpen ? "Collapse diagnostics" : "Expand diagnostics"}
          data-testid="admin-matches-card-expand"
          className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded border border-rule text-ink-muted transition-colors hover:bg-field hover:text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-canvas"
        >
          {isOpen ? (
            <ChevronUp size={16} strokeWidth={2} aria-hidden="true" />
          ) : (
            <ChevronDown size={16} strokeWidth={2} aria-hidden="true" />
          )}
        </button>
      </div>

      <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
        <div className="rounded border border-rule/60 bg-elevated px-2 py-1.5">
          <span className="block font-display text-[0.6rem] uppercase tracking-wider text-ink-muted">
            Format
          </span>
          <FormatPill format={match.format} />
        </div>
        <div className="rounded border border-rule/60 bg-elevated px-2 py-1.5">
          <span className="block font-display text-[0.6rem] uppercase tracking-wider text-ink-muted">
            Watching
          </span>
          <span
            data-testid="admin-matches-card-watchers"
            className={watchers > 0 ? "font-mono text-accent" : "font-mono text-ink-muted"}
          >
            {watchers}
          </span>
        </div>
        <div className="rounded border border-rule/60 bg-elevated px-2 py-1.5">
          <span className="block font-display text-[0.6rem] uppercase tracking-wider text-ink-muted">
            Score
          </span>
          <span className="font-mono text-ink">
            {match.score_home == null || match.score_away == null
              ? "—"
              : `${match.score_home}–${match.score_away}`}
          </span>
        </div>
        <div className="rounded border border-rule/60 bg-elevated px-2 py-1.5">
          <span className="block font-display text-[0.6rem] uppercase tracking-wider text-ink-muted">
            Slots
          </span>
          <div className="mt-1 flex flex-wrap gap-1">
            {slots.length === 0 ? (
              <span className="text-ink-muted text-[0.6rem]">none</span>
            ) : (
              slots.map((slot) => (
                <SlotPill
                  key={slot}
                  slot={slot}
                  status={match.video_status?.[slot] ?? "none"}
                />
              ))
            )}
          </div>
        </div>
      </div>

      <div className="mt-3 flex flex-wrap gap-1 border-t border-rule/60 pt-2">
        <AdminIconLink
          href={`/match/${match.slug}`}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="admin-matches-card-open"
          label="Open match"
          icon={ExternalLink}
        />
        <AdminIconButton
          onClick={onEdit}
          data-testid="admin-matches-card-edit"
          label="Edit match"
          icon={Pencil}
        />
        <AdminIconButton
          onClick={onUpload}
          data-testid="admin-matches-card-upload"
          label="Upload video"
          icon={Upload}
          tone="accent"
        />
        <AdminIconButton
          onClick={onDelete}
          data-testid="admin-matches-card-delete"
          label="Delete match"
          icon={Trash2}
          tone="destructive"
        />
      </div>

      {isOpen ? (
        <div
          data-testid="admin-matches-card-diagnostics"
          data-match-id={match.id}
          className="mt-3 border-t border-rule/60 pt-3"
        >
          <MatchSlotPanel match={match} />
        </div>
      ) : null}
    </article>
  );
}

/** Inline season label rendered under the matchup cell. Seasons are
 * globally shared, so the name is resolved from the /api/me payload's
 * top-level `seasons` array. Cheap — no extra fetch. */
function SeasonLabel({ match }: { match: Match }) {
  const seasonName = useAuthStore((s) => {
    if (!s.me || !match.season_id) return null;
    return s.me.seasons.find((sn) => sn.id === match.season_id)?.name ?? null;
  });
  if (!seasonName) return null;
  return (
    <div
      data-testid="admin-matches-row-season"
      className="text-[0.6rem] text-ink-muted"
    >
      Season: {seasonName}
    </div>
  );
}

function FormatPill({ format }: { format: string }) {
  const display = format === "two_halves" ? "Halves" : format === "full" ? "Full" : format;
  return (
    <span className="rounded border border-rule bg-surface px-1.5 py-0.5 text-[0.6rem] uppercase tracking-wider text-ink-muted">
      {display}
    </span>
  );
}

function SlotPill({ slot, status }: { slot: string; status: string }) {
  const tone =
    status === "ready"
      ? "border-good/40 text-good"
      : status === "transcoding"
        ? "border-accent text-accent"
        : status === "error"
          ? "border-destructive/40 text-destructive"
          : "border-rule text-ink-muted";
  return (
    <span
      data-testid="admin-matches-slot-pill"
      data-slot={slot}
      data-slot-status={status}
      className={`rounded border bg-surface px-1.5 py-0.5 text-[0.6rem] font-mono ${tone}`}
    >
      {slot.replace("_", " ")} · {status}
    </span>
  );
}

async function invalidateAdminMatches(queryClient: ReturnType<typeof useQueryClient>) {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: unscopedKey("public-matches") }),
    queryClient.invalidateQueries({ queryKey: unscopedKey("admin-matches") }),
    queryClient.invalidateQueries({
      queryKey: unscopedKey("admin-matches-allowed"),
    }),
    queryClient.invalidateQueries({ queryKey: ["admin-matches-scoped"] }),
    queryClient.invalidateQueries({ queryKey: ["coach-matches"] }),
  ]);
}

function DeleteMatchConfirm({
  match,
  onClose,
}: {
  match: Match;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const onConfirm = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await apiFetch(`/api/matches/${encodeURIComponent(match.id)}`, {
        method: "DELETE",
        scope: false,
      });
      await invalidateAdminMatches(queryClient);
      onClose();
    } catch (err) {
      if (err instanceof ApiError) {
        setError(
          typeof err.detail === "string"
            ? err.detail
            : "Failed to delete match.",
        );
      } else {
        setError("Failed to delete match.");
      }
      setSubmitting(false);
    }
  };

  return (
    <ConfirmDialog
      title="Delete match?"
      message={
        <>
          <p>
            <strong>
              {match.home_team || "—"} vs {match.away_team || "—"}
            </strong>{" "}
            ({match.date || "no date"}) will be permanently removed along with
            all uploaded video, HLS variants, and coaching notes referencing
            it.
          </p>
          {error ? (
            <p
              data-testid="admin-matches-delete-error"
              className="mt-2 text-destructive font-mono text-xs"
            >
              {error}
            </p>
          ) : null}
        </>
      }
      confirmLabel={submitting ? "Deleting…" : "Delete match"}
      danger
      onConfirm={onConfirm}
      onClose={onClose}
      data-testid="admin-matches-delete-confirm"
    />
  );
}

function countVodViewers(
  active: AdminStreamSession[] | undefined,
  matchId: string,
): number {
  if (!active) return 0;
  return active.filter((s) => s.match_id === matchId && s.kind !== "live")
    .length;
}

/** Aggregate a match's per-slot statuses into a single bucket the
 * filter dropdown can use. */
function deriveAggregateStatus(
  match: Match,
): "none" | "ready" | "transcoding" | "error" | "partial" {
  const statuses = Object.values(match.video_status ?? {});
  if (statuses.length === 0) return "none";
  if (statuses.every((s) => s === "ready")) return "ready";
  if (statuses.some((s) => s === "transcoding")) return "transcoding";
  if (statuses.some((s) => s === "error")) return "error";
  return "partial";
}

function deriveFormatGroup(match: Match): "full" | "two_halves" | "other" {
  if (match.format === "full") return "full";
  if (match.format === "two_halves") return "two_halves";
  return "other";
}
